import { Directive, ElementRef, HostListener, Input, Renderer2, Optional, Self, OnInit, OnDestroy } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

const DEFAULT_LOCALE = (() => {
  try {
    const cfg = JSON.parse(localStorage.getItem('appConfig') ?? '{}') || {};
    return cfg.locale || 'en-US';
  } catch {
    return 'en-US';
  }
})();

@Directive({
  selector: '[appNumberFormat]'
})
export class NumberFormatDirective implements OnInit, OnDestroy {
  @Input('appNumberFormat') allowDecimal = true; // <input [appNumberFormat]="true">
  @Input() locale: string = DEFAULT_LOCALE;

  private lastValue = '';

  private controlSub?: Subscription;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2,
    @Optional() @Self() private control?: NgControl
  ) {}

  ngOnInit(): void {
    // If form control exists, subscribe to programmatic changes so we can format them
    if (this.control && this.control.control) {
      this.controlSub = this.control.control.valueChanges?.subscribe((val: any) => {
        // Avoid interfering while the user is typing: only update view when value comes from model
        const input = this.el.nativeElement;
        const str = val === null || val === undefined ? '' : String(val);
        // 'clean' expects a string with digits and dot; if model is numeric, convert
        const cleaned = this.clean(str);
        const formatted = this.formatValue(cleaned);
        this.renderer.setProperty(input, 'value', formatted);
      });
    }
    // Also format the initial value shown in the input when the directive initializes.
    // We only modify the view (not the model) so opening an edit form shows the formatted value immediately.
    try {
      const inputEl = this.el.nativeElement;
      const initVal = inputEl.value ?? (this.control && this.control.control ? this.control.control.value : '');
      const initStr = initVal === null || initVal === undefined ? '' : String(initVal);
      const cleanedInit = this.clean(initStr);
      const formattedInit = this.formatValue(cleanedInit);
      if (formattedInit !== undefined && formattedInit !== null) {
        this.renderer.setProperty(inputEl, 'value', formattedInit);
      }
    } catch (e) {
      // swallow errors to avoid breaking forms
    }
  }

  private clean(value: string): string {
    if (!value) return '';
    return value.replace(/[^0-9\-\.]/g, '');
  }

  private formatIntegerPart(intPart: string): string {
    if (intPart === '' || intPart === '-') return intPart;
    const negative = intPart.startsWith('-');
    const numeric = negative ? intPart.slice(1) : intPart;
    const numericForFormat = numeric.replace(/^0+(?=\d)/, '');
    if (numericForFormat === '') return negative ? '-0' : '0';
    const formatted = new Intl.NumberFormat(this.locale).format(parseInt(numericForFormat, 10));
    return negative ? '-' + formatted : formatted;
  }

  private formatValue(cleaned: string): string {
    if (!cleaned) return '';
    const parts = cleaned.split('.');
    const intPart = parts[0] ?? '';
    const decPart = parts[1] ?? null;
    const formattedInt = this.formatIntegerPart(intPart);
    if (this.allowDecimal && decPart !== null) {
      return formattedInt + '.' + decPart;
    } else {
      return formattedInt;
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: InputEvent) {
    const input = this.el.nativeElement;
    const raw = input.value;
    const selectionStart = input.selectionStart ?? raw.length;
    const cleaned = this.clean(raw);
    const formatted = this.formatValue(cleaned);
    const digitsLeft = (raw.slice(0, selectionStart).match(/[0-9]/g) || []).length;
    this.renderer.setProperty(input, 'value', formatted);
    this.lastValue = formatted;
    let newPos = 0;
    let digitsSeen = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (/[0-9]/.test(formatted[i])) digitsSeen++;
      newPos = i + 1;
      if (digitsSeen >= digitsLeft) break;
    }
    if (digitsLeft === 0) newPos = 0;
    if (digitsSeen < digitsLeft) newPos = formatted.length;
    try { input.setSelectionRange(newPos, newPos); } catch {}

    // update underlying FormControl with numeric value (unformatted)
    if (this.control && this.control.control) {
      const numeric = cleaned === '' || cleaned === '-' ? null : Number(cleaned);
      // update model but prevent Angular from writing model back into the view
      this.control.control.setValue(numeric, { emitEvent: true, emitModelToViewChange: false });
      // ensure view remains formatted
      this.renderer.setProperty(input, 'value', formatted);
    }
  }

  @HostListener('blur')
  onBlur() {
    const input = this.el.nativeElement;
    let value = input.value;
    if (!value) return;
    if (this.allowDecimal && value.endsWith('.')) {
      value = value.slice(0, -1);
      this.renderer.setProperty(input, 'value', value);
    }

    // ensure FormControl has numeric value on blur as well
    if (this.control && this.control.control) {
      const cleaned = this.clean(this.el.nativeElement.value);
      const numeric = cleaned === '' || cleaned === '-' ? null : Number(cleaned);
      this.control.control.setValue(numeric, { emitEvent: true, emitModelToViewChange: false });
      // keep formatted display
      const formatted = this.formatValue(cleaned);
      this.renderer.setProperty(this.el.nativeElement, 'value', formatted);
    }
  }

  ngOnDestroy(): void {
    this.controlSub?.unsubscribe();
  }
}
